/**
 * Account-level settings for Account Admins and Platform Admins.
 * Provides tabbed interface for managing teams, users, and roles
 * within an account.
 */
import { FlaskConical, Shield, User, Users } from "lucide-react";

import { TeamsTab } from "./account/teams";
import { BetaAccessTab } from "./user-management/beta";
import { RolesTab } from "./user-management/roles";
import { UsersTab } from "./user-management/users";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthStore } from "@/stores/auth";


/**
 * Account Settings page for account administrators.
 * Provides tabbed interface for managing:
 * - Teams: Create, edit, delete teams within the account
 * - Users: View and manage users, invites, and role assignments
 * - Roles: Create, edit, delete custom roles with capabilities
 */
export function AccountSettingsPage() {
	const { accounts, user } = useAuthStore();
	// Beta enrolment is platform-admin-only, so the matrix tab is shown only to
	// a super_admin viewer; the server also denies the mutation for anyone else.
	const isPlatformAdmin = user?.role === "super_admin";

	if (accounts.length === 0) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">
					No accounts available. Contact your administrator.
				</p>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-bold">Account Settings</h1>
				<p className="text-muted-foreground">
					Manage teams, users, and roles for your account
				</p>
			</div>

			{/* Tabbed Interface */}
			<Tabs defaultValue="teams" className="space-y-4">
				<TabsList>
					<TabsTrigger value="teams">
						<Users className="mr-2 h-4 w-4" />
						Teams
					</TabsTrigger>
					<TabsTrigger value="users">
						<User className="mr-2 h-4 w-4" />
						Users
					</TabsTrigger>
					<TabsTrigger value="roles">
						<Shield className="mr-2 h-4 w-4" />
						Roles
					</TabsTrigger>
					{isPlatformAdmin && (
						<TabsTrigger value="beta">
							<FlaskConical className="mr-2 h-4 w-4" />
							Beta Access
						</TabsTrigger>
					)}
				</TabsList>

				<TabsContent value="teams">
					<TeamsTab />
				</TabsContent>

				<TabsContent value="users">
					<UsersTab />
				</TabsContent>

				<TabsContent value="roles">
					<RolesTab />
				</TabsContent>

				{isPlatformAdmin && (
					<TabsContent value="beta">
						<BetaAccessTab />
					</TabsContent>
				)}
			</Tabs>
		</div>
	);
}
